import { currentYear } from "@/lib/siteYear";
// SEO/AIO landing config for /chainsaws/category/:slug.

export interface ChainsawRow {
  id: string;
  brand: string;
  model: string;
  slug: string;
  power_type: string; // petrol | cordless | electric
  chainsaw_type?: string | null;
  description?: string | null;
}

export interface ChainsawSubcategoryConfig {
  slug: string;
  title: string;
  shortLabel: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  longCopy: string[];
  faqs: { q: string; a: string }[];
  filter: (c: ChainsawRow) => boolean;
}

const Y = currentYear();

export const chainsawSubcategories: Record<string, ChainsawSubcategoryConfig> = {
  "electric-chainsaws": {
    slug: "electric-chainsaws",
    title: "Electric Chainsaws",
    shortLabel: "Electric",
    h1: `Best Electric Chainsaws UK ${Y}`,
    metaTitle: `Best Electric Chainsaws UK ${Y} | Corded Chainsaw Reviews`,
    metaDescription:
      "Independent UK reviews of the best electric (corded) chainsaws. Compare top mains-powered models for light pruning, log cutting and garden work.",
    intro:
      "Corded electric chainsaws are the quiet, low-maintenance choice for homeowners who want serious cutting power without fuel, fumes or charging. Plug in and you have unlimited runtime — perfect for log piles, pruning sessions and storm clean-ups close to a power outlet.",
    longCopy: [
      "Electric chainsaws strip out the two biggest hassles of petrol — fuel mixing and recoil starting — while delivering more sustained power than most cordless models. A 2000W corded saw matches a 35–40cc petrol chainsaw for cutting through hardwood, at a fraction of the price.",
      "Look for a brushless or induction motor (longer life), tool-less chain tensioning, automatic bar oiling and a chain brake. Bar lengths of 35–40 cm cover most homeowner jobs; 45 cm is the sweet spot for firewood.",
      "Top UK brands include Bosch, Makita, Einhell and Stihl. Expect to pay £80–£200 for a quality corded electric chainsaw — significantly less than a comparable petrol or cordless model.",
    ],
    faqs: [
      {
        q: "Are electric chainsaws any good?",
        a: "Yes — modern corded electric chainsaws cut as well as a small-to-mid petrol saw on softwood and hardwood up to 30 cm in diameter, with much lower noise, no fumes and minimal maintenance.",
      },
      {
        q: "What size electric chainsaw do I need?",
        a: "For light pruning, a 30–35 cm bar with a 1600–1800W motor is plenty. For firewood and storm work, choose a 40 cm bar with a 2000W+ motor.",
      },
      {
        q: "Are electric chainsaws safer than petrol?",
        a: "They're generally easier to handle thanks to lower weight and lower vibration, and there's no fuel risk. But the chain is just as sharp — always wear chainsaw trousers, gloves, helmet and steel-toe boots.",
      },
    ],
    filter: c => c.power_type === "electric",
  },

  "petrol-chainsaws": {
    slug: "petrol-chainsaws",
    title: "Petrol Chainsaws",
    shortLabel: "Petrol",
    h1: `Best Petrol Chainsaws UK ${Y}`,
    metaTitle: `Best Petrol Chainsaws UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "The best petrol chainsaws in the UK reviewed and ranked. Compare Stihl, Husqvarna, Echo and more for firewood, felling and professional tree work.",
    intro:
      "Petrol chainsaws deliver the raw power and unlimited runtime you need for serious cutting — firewood processing, felling, large-scale storm work and full-day pro use. Nothing else matches the torque-to-weight ratio of a well-tuned 50cc saw.",
    longCopy: [
      "Petrol chainsaws split into three classes: homeowner (30–40cc, 35–40 cm bar), farm & ranch (40–55cc, 40–50 cm bar) and professional (55cc+, 50–70 cm bar). Match the class to the wood you'll actually cut.",
      "Stihl and Husqvarna dominate the UK market, with Echo and Makita strong in the homeowner segment. Look for anti-vibration mounts, side-access chain tensioning, magnesium crankcases on pro saws, and Easy2Start systems for low-effort pull starts.",
      "Use a 50:1 fuel mix with quality 2-stroke oil and fresh E5 unleaded. Sharpen the chain little and often — a sharp 35cc saw outcuts a blunt 55cc saw every time.",
    ],
    faqs: [
      {
        q: "What size petrol chainsaw do I need for firewood?",
        a: "For seasoning rounds up to 30 cm diameter, a 40–45cc saw with a 38–40 cm bar is ideal. For mixed firewood and small felling, step up to a 50–55cc saw with a 45 cm bar.",
      },
      {
        q: "How long do petrol chainsaws last?",
        a: "A well-maintained pro Stihl or Husqvarna saw will easily last 15–20 years of regular use. Homeowner saws typically last 8–12 years with annual servicing.",
      },
      {
        q: "Do petrol chainsaws need running in?",
        a: "Yes — modern petrol chainsaws benefit from 3–5 tanks of light cutting before sustained heavy work. Avoid lugging the engine and let it cool between cuts during the running-in period.",
      },
    ],
    filter: c => c.power_type === "petrol",
  },

  "cordless-chainsaws": {
    slug: "cordless-chainsaws",
    title: "Cordless Chainsaws",
    shortLabel: "Cordless",
    h1: `Best Cordless Chainsaws UK ${Y}`,
    metaTitle: `Best Cordless Chainsaws UK ${Y} | Battery Chainsaw Reviews`,
    metaDescription:
      "Independent UK reviews of the best cordless (battery) chainsaws. Compare Makita, DeWalt, Stihl, EGO and Milwaukee battery saws for pruning, firewood and pro work.",
    intro:
      "Cordless chainsaws combine the silence and clean operation of electric with the freedom of petrol. Modern 36V, 40V and twin-18V platforms now rival small petrol saws on cutting power — without the fuel, fumes, or recoil starting.",
    longCopy: [
      "Battery voltage tells you peak power; Ah (battery capacity) tells you runtime. A 5Ah 36V cordless chainsaw typically cuts 30–60 logs of seasoned hardwood per charge — easily enough for a morning's firewood work.",
      "Twin-battery 2x18V saws from Makita, DeWalt and Bosch let you share batteries across your wider tool kit. EGO's 56V Arc Lithium platform and Stihl's AP/AS systems offer the most cutting power in the consumer cordless segment.",
      "Look for a brushless motor (longer life, more torque), tool-less chain tensioning, automatic oiling, and a chain brake. For top-handle work in trees, choose a dedicated certified top-handle model — never use a rear-handle saw one-handed.",
    ],
    faqs: [
      {
        q: "Are cordless chainsaws powerful enough for firewood?",
        a: "Yes — modern 36V+ cordless chainsaws easily process firewood up to 30 cm diameter. For heavy firewood production or large felling, petrol still has the edge on sustained power.",
      },
      {
        q: "How long does a cordless chainsaw battery last?",
        a: "A 5Ah battery typically runs for 30–60 minutes of mixed cutting. Most users get through a morning of pruning or 20–40 firewood cuts per charge.",
      },
      {
        q: "Which cordless chainsaw brand is best?",
        a: "Stihl AP, EGO 56V and Makita XGT are the top performers on raw cutting power. For shared-battery convenience, Makita LXT, DeWalt XR and Bosch 18V are excellent if you already own those tools.",
      },
    ],
    filter: c => c.power_type === "cordless",
  },
};

export const chainsawSubcategoryList = Object.values(chainsawSubcategories);
